// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Combobox,
  Option,
  Tooltip,
  type OptionOnSelectData,
  type SelectionEvents,
  Caption1,
} from '@fluentui/react-components';
import { useIntl } from 'react-intl';
import { useAuth } from '../auth/useAuth';
import { apiUrl } from '../utils/api-url';
import { residencyLabel } from '../utils/residency';

interface TenantInfo {
  customerId: string;
  name: string;
  /** Entra tenant GUID — the canonical identity key for dedupe (#2403). */
  tenantId?: string;
  /** Resolved data-residency region (e.g. "northeurope") — see #1668. */
  residencyRegion?: string;
  /** Verified domain names — lets an operator find a tenant by typing a domain (#2883). */
  domains?: string[];
}

/**
 * Filter tenants by an organisation-name OR verified-domain substring (#2883). A null/empty query
 * returns the full list. Exported for deterministic unit testing of the domain-search logic.
 */
export function filterTenantsByQuery<T extends { name: string; domains?: string[] }>(
  tenants: T[], query: string | null,
): T[] {
  const q = query?.trim().toLowerCase() ?? '';
  if (!q) return tenants;
  return tenants.filter(t =>
    t.name.toLowerCase().includes(q) ||
    (t.domains ?? []).some(d => d.toLowerCase().includes(q)));
}

interface TenantPickerProps {
  currentCustomerId: string;
  onTenantChange: (customerId: string) => void;
}

/**
 * Initials for the organisation identity chip (#5859) — first letter of the first two
 * words, so "Global Micro Solutions" reads "GM". Falls back to the first two characters
 * for a single-word name.
 */
export function tenantInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Header customer/tenant picker for MSP users. Loads available tenants from
 * GET /api/settings/tenants. Renders the empty-tenant caption when none are
 * onboarded — degrades gracefully if the endpoint is unavailable.
 *
 * Fetches with a bearer token from useAuth so it does not depend on any
 * per-agent api-client contract. Shared component — Shared-CSS-Reference v3.1 §2.1.
 */
export const TenantPicker = ({ currentCustomerId, onTenantChange }: TenantPickerProps) => {
  const intl = useIntl();
  const { getAccessToken } = useAuth();
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadTenants = async () => {
      try {
        const token = await getAccessToken();
        const res = await fetch(apiUrl('/api/settings/tenants'), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as { tenants?: TenantInfo[] };
          // Defensive dedup by tenantId — one Entra tenant must appear once even if
          // Cosmos drifts to two aliases for it (#2403: the 'secure'/'scs' split-brain
          // slipped past the old customerId-keyed dedup). Falls back to customerId when
          // tenantId is absent. Backend already collapses these (#1344, #2403); this is
          // the belt-and-braces second line.
          const seen = new Set<string>();
          const unique = (data.tenants ?? []).filter(t => {
            const key = (t.tenantId || t.customerId).toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          unique.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
          setTenants(unique);
        }
      } catch {
        // Non-critical — the user can still use the default tenant.
      } finally {
        setIsLoading(false);
      }
    };
    void loadTenants();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select first tenant when current selection is not in the list
  const stableOnTenantChange = useCallback(onTenantChange, [onTenantChange]);
  useEffect(() => {
    if (isLoading || tenants.length === 0) return;
    const currentInList = tenants.some(t => t.customerId === currentCustomerId);
    if (!currentInList) {
      stableOnTenantChange(tenants[0].customerId);
    }
  }, [isLoading, tenants, currentCustomerId, stableOnTenantChange]);

  // Domain-aware search affordance (#2883): the operator can type either an organisation name or
  // any verified domain (e.g. "globalmicro.co.za") to find a tenant. `query` is null when not
  // actively typing — the input then shows the selected tenant's name. The reference key emitted on
  // select stays the customerId/alias; domain text is a filter only, never the key.
  const [query, setQuery] = useState<string | null>(null);

  const selectedName = tenants.find(t => t.customerId === currentCustomerId)?.name ?? currentCustomerId;

  const filteredTenants = useMemo(() => filterTenantsByQuery(tenants, query), [tenants, query]);

  const onSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    const selectedId = data.optionValue;
    if (selectedId) {
      onTenantChange(selectedId);
      // Reset ONLY on a real selection — Fluent's freeform Combobox fires onOptionSelect with
      // optionValue=undefined (clearSelection) on every non-prefix-matching keystroke; resetting
      // there would wipe the typed domain each keystroke and break search (#2883 review #1).
      setQuery(null);
    }
  };

  if (isLoading) {
    return null;
  }

  if (tenants.length === 0) {
    return (
      <div className="flex-row">
        <Caption1>{intl.formatMessage({ id: 'tenantPicker.label' })}</Caption1>
        <Caption1 className="text-muted">{intl.formatMessage({ id: 'tenantPicker.noTenants' })}</Caption1>
      </div>
    );
  }

  // Inline region code + full residency statement in the tooltip (#5859, closed ruling 9:
  // the residency CHIP is retired — badge treatment is reserved for the deviation state).
  const residency = residencyLabel(tenants.find(t => t.customerId === currentCustomerId)?.residencyRegion);

  return (
    <div className="flex-row tenant-switcher">
      {/* Louder tenant identity replaces the retired pin (closed ruling 10): the selection is
          remembered per user/device by the caller, so there is no pin affordance here. */}
      <span className="tenant-initials" aria-hidden="true">{tenantInitials(selectedName)}</span>
      <Combobox
        aria-label={intl.formatMessage({ id: 'tenantPicker.label' })}
        size="small"
        freeform
        value={query ?? selectedName}
        selectedOptions={[currentCustomerId]}
        onOptionSelect={onSelect}
        onChange={(e) => setQuery((e.target as HTMLInputElement).value)}
        onBlur={() => setQuery(null)}
        placeholder={intl.formatMessage({ id: 'tenantPicker.searchPlaceholder' })}
        style={{ minInlineSize: '220px' }}
      >
        {filteredTenants.map(t => (
          <Option key={t.customerId} value={t.customerId} text={t.name}>
            {t.name}
          </Option>
        ))}
        {filteredTenants.length === 0 && (
          <Option key="__none" value="" text="" disabled>
            {intl.formatMessage({ id: 'tenantPicker.noMatch' })}
          </Option>
        )}
      </Combobox>
      {residency && (
        <Tooltip
          content={intl.formatMessage({ id: 'tenantPicker.residency' }, { region: residency.full })}
          relationship="description"
        >
          {/* #5871 — the region code is a Latin island; `bdi` stops the separator and the
              code swapping places when the shell mirrors. */}
          <span className="tenant-region">{'· '}<bdi>{residency.short}</bdi></span>
        </Tooltip>
      )}
    </div>
  );
};
